/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import { FormikProvider, useFormik } from 'formik'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  API_TOKEN_FIELD_MAP,
  API_TOKEN_FIELD_TITLES,
  apiTokenInitialValues,
  applyApiAccessError,
  toApiTokenPayload,
  type AccountApiAccess,
  type ApiTokenFormValues,
} from '@sportninja/common/features/account/apiAccess'
import CopyComponent from 'src/components/CopyComponent'
import BaseModal from 'src/components/ts-components/BaseModal'
import Text from 'src/components/ts-components/Text'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { SwitchField, TextField } from '../../components/FormikField'
import { useApiTokenMutations } from '../hooks/useApiTokenMutations'

interface ApiTokenModalProps {
  isOpen: boolean
  onClose: () => void
  accountUid: string
  /** Which abilities the account's API features allow choosing. */
  access: AccountApiAccess
}

const NOT_ENABLED_HINT =
  'Not enabled for this account. Contact SportNinja to turn it on.'

/**
 * Names and creates an API token, then shows its secret — the only time the
 * API ever returns it — with a copy button, until the user closes the modal.
 */
const ApiTokenModal = (props: ApiTokenModalProps) =>
  // Mounted only while open, so a revealed secret never survives a reopen.
  props.isOpen ? <ApiTokenForm {...props} /> : null

const ApiTokenForm = ({
  isOpen,
  onClose,
  accountUid,
  access,
}: ApiTokenModalProps) => {
  const { message } = App.useApp()
  const { create } = useApiTokenMutations(accountUid)
  const [plainTextToken, setPlainTextToken] = useState<string | null>(null)

  const formik = useFormik<ApiTokenFormValues>({
    initialValues: apiTokenInitialValues(access),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setErrors, setTouched }) => {
      try {
        const created = await create.mutateAsync(toApiTokenPayload(values))
        setPlainTextToken(created.plainTextToken)
        message.success(
          formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.apiTokenCreated)
        )
      } catch (err) {
        applyApiAccessError(err, {
          fieldMap: API_TOKEN_FIELD_MAP,
          titleMap: API_TOKEN_FIELD_TITLES,
          setErrors,
          setTouched: (touched) => setTouched(touched, false),
          notifyError: message.error,
        })
      }
    },
  })

  if (plainTextToken) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title={capitalizeWords('Copy Your API Token')}
        onOk={onClose}
        okText='Done'
      >
        <div css={revealStyle}>
          <Text variant='paragraphSmall' color={colors.NEUTRAL_0}>
            Copy this token now and store it somewhere safe. For your security
            it will not be shown again.
          </Text>
          <CopyComponent
            id={plainTextToken}
            customText='TOKEN'
            alternativeBackground
          />
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
            Send it as a Bearer token to the /v1/api-token API. It acts as you,
            with your permissions, limited to the access you chose.
          </Text>
        </div>
      </BaseModal>
    )
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={capitalizeWords('Create API Token')}
      onOk={formik.submitForm}
      okText='Create'
      isLoading={formik.isSubmitting}
    >
      <FormikProvider value={formik}>
        <div css={formStyle}>
          <TextField
            name='name'
            label={capitalizeWords('Name')}
            placeholder={capitalizeWords('e.g. stats integration')}
            hint='A name to recognize where this token is used.'
          />
          <SwitchField
            name='canRead'
            label={capitalizeWords('Read')}
            hint={
              access.canRead
                ? 'Read schedules, teams, games and stats.'
                : NOT_ENABLED_HINT
            }
            disabled={!access.canRead}
          />
          <SwitchField
            name='canWrite'
            label={capitalizeWords('Write')}
            hint={
              access.canWrite
                ? 'Create, change and delete data.'
                : NOT_ENABLED_HINT
            }
            disabled={!access.canWrite}
          />
        </div>
      </FormikProvider>
    </BaseModal>
  )
}

export default ApiTokenModal

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const revealStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
